import { inject, Injectable } from '@angular/core';
import { EInitEventTypeCode } from '@app/core/enums/init-event-type.enum';
import {
  IInitEventDisruptionProtocol,
  IInitEventDisruptionPvsTask,
  REACTIVE_API_TOPICS,
  ReactiveApiBrokerService,
} from 'ngx-mt-ui-lib/api-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  filter,
  from,
  merge,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import {
  buildEventCardProtocolsSyncActions,
  resetEventCardGanttDedup,
} from '../event-card-protocols-sync';
import { EventCardActions } from './event-card.actions';

@Injectable()
export class EventCardReactiveEffects {
  private readonly actions$ = inject(Actions);
  private readonly broker = inject(ReactiveApiBrokerService);
  private readonly translateService = inject(TranslateService);

  readonly stopWatchEventCardReactive$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventCardActions.stopWatchEventCardReactive),
        tap(() => resetEventCardGanttDedup()),
      ),
    { dispatch: false },
  );

  readonly watchEventCardReactive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventCardActions.watchEventCardReactive),
      switchMap(({ eventId, eventType }) => {
        if (
          eventType !== EInitEventTypeCode.NNR ||
          typeof eventId !== 'number' ||
          !eventId
        ) {
          return EMPTY;
        }

        resetEventCardGanttDedup();

        const stop$ = this.actions$.pipe(
          ofType(EventCardActions.stopWatchEventCardReactive),
        );

        const protocols$ = this.broker
          .watchParsed<
            IInitEventDisruptionProtocol[]
          >(REACTIVE_API_TOPICS.disruptionProtocols(eventId))
          .pipe(
            filter((protocols): protocols is IInitEventDisruptionProtocol[] =>
              Array.isArray(protocols),
            ),
            mergeMap((protocols) =>
              from(
                buildEventCardProtocolsSyncActions(
                  eventId,
                  protocols,
                  this.translateService,
                ),
              ),
            ),
          );

        const pvs$ = this.broker
          .watchParsed<
            IInitEventDisruptionPvsTask[]
          >(REACTIVE_API_TOPICS.disruptionPvsTaskList(eventId))
          .pipe(
            filter(
              (pvsTaskList): pvsTaskList is IInitEventDisruptionPvsTask[] =>
                Array.isArray(pvsTaskList),
            ),
            mergeMap((eventPvsTaskList) =>
              from([
                EventCardActions.requestEventPvsTaskListSuccess({
                  eventPvsTaskList,
                }),
              ]),
            ),
          );

        return merge(protocols$, pvs$).pipe(takeUntil(stop$));
      }),
    ),
  );
}
